import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, X, KeyRound, BadgeCheck, Pencil, Mail, Eye, Activity, Copy } from "lucide-react";
import api, { apiError } from "../lib/api";

const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40";
const PLAN_OPTIONS = [
  ["student_annual", "Student Annual — £27/year"],
  ["pro_annual", "Pro Annual — £47/year"],
  ["lifetime", "Lifetime — £99 one-off"],
];
const BADGE = {
  free: "bg-slate-200 text-slate-600",
  student: "bg-sky-100 text-sky-800",
  pro: "bg-emerald-100 text-emerald-800",
  lifetime: "bg-amber-100 text-amber-800",
};

function AddMemberModal({ onSaved, onClose }) {
  const [f, setF] = useState({ name: "", email: "", password: "", plan: "pro_annual",
    voucher_code: "", payment_received: true, payment_reference: "", send_welcome: true });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/members", { ...f, password: f.password || null, voucher_code: f.voucher_code || null, payment_reference: f.payment_reference || null });
      toast.success(f.send_welcome ? "Member created — welcome email sent" : "Member created");
      onSaved(); onClose();
    } catch (e2) { toast.error(apiError(e2)); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-6 w-full max-w-md my-8 space-y-3" data-testid="member-add-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">Register a member</h3>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div>
          <label className="text-xs font-bold text-slate-500">Full name *</label>
          <input className={inputCls} required value={f.name} onChange={set("name")} data-testid="member-add-name" />
        </div>
        <div>
          <label className="text-xs font-bold text-slate-500">Email *</label>
          <input className={inputCls} type="email" required value={f.email} onChange={set("email")} data-testid="member-add-email" />
        </div>
        <div>
          <label className="text-xs font-bold text-slate-500">Password (optional)</label>
          <input className={inputCls} value={f.password} onChange={set("password")} placeholder="Leave blank — they set it via the welcome email" data-testid="member-add-password" />
        </div>
        <div>
          <label className="text-xs font-bold text-slate-500">Membership *</label>
          <select className={inputCls} value={f.plan} onChange={set("plan")} data-testid="member-add-plan">
            {PLAN_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-bold text-slate-500">Voucher code (optional)</label>
          <input className={inputCls} value={f.voucher_code} onChange={set("voucher_code")} placeholder="e.g. lifetime99" data-testid="member-add-voucher" />
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <input type="checkbox" checked={f.payment_received} onChange={set("payment_received")} data-testid="member-add-payment-received" />
            Payment received independently
          </label>
          <input className={inputCls} value={f.payment_reference} onChange={set("payment_reference")} placeholder="Payment reference / note (e.g. bank transfer 16 Jun)" data-testid="member-add-payment-ref" />
        </div>
        <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <input type="checkbox" checked={f.send_welcome} onChange={set("send_welcome")} data-testid="member-add-send-welcome" />
          Send welcome email with set-password link
        </label>
        <button type="submit" disabled={busy} className="w-full bg-slate-900 text-white font-bold py-2.5 rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50" data-testid="member-add-save">
          {busy ? "Creating…" : "Create & activate member"}
        </button>
      </form>
    </div>
  );
}

function EditMemberModal({ member, onSaved, onClose }) {
  const [f, setF] = useState({ name: member.name || "", email: member.email || "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/admin/members/${member.id}`, f);
      toast.success("Member details updated");
      onSaved(); onClose();
    } catch (e2) { toast.error(apiError(e2)); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-6 w-full max-w-sm my-8 space-y-3" data-testid="member-edit-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">Edit member details</h3>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div>
          <label className="text-xs font-bold text-slate-500">Full name</label>
          <input className={inputCls} required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="member-edit-name" />
        </div>
        <div>
          <label className="text-xs font-bold text-slate-500">Email</label>
          <input className={inputCls} type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="member-edit-email" />
        </div>
        <button type="submit" disabled={busy} className="w-full bg-slate-900 text-white font-bold py-2.5 rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50" data-testid="member-edit-save">
          {busy ? "Saving…" : "Save details"}
        </button>
      </form>
    </div>
  );
}

function PlanModal({ member, onSaved, onClose }) {
  const [plan, setPlan] = useState("pro_annual");
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/admin/members/${member.id}/membership`, { plan, payment_reference: ref || null });
      toast.success("Membership updated");
      onSaved(); onClose();
    } catch (e2) { toast.error(apiError(e2)); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-6 w-full max-w-sm my-8 space-y-3" data-testid="member-plan-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">Change membership</h3>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <p className="text-sm text-slate-500">{member.name} — {member.email}</p>
        <select className={inputCls} value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="member-plan-select">
          {PLAN_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          <option value="free">Revoke membership (free)</option>
        </select>
        <input className={inputCls} value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Payment reference / note (optional)" data-testid="member-plan-ref" />
        <button type="submit" disabled={busy} className="w-full bg-slate-900 text-white font-bold py-2.5 rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50" data-testid="member-plan-save">
          {busy ? "Saving…" : "Update membership"}
        </button>
      </form>
    </div>
  );
}

export default function AdminMembers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [planFor, setPlanFor] = useState(null);
  const [editFor, setEditFor] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [revealedCode, setRevealedCode] = useState(null); // {email, code, expires_at}
  const [emailHealth, setEmailHealth] = useState(null);   // diagnostic result
  const [healthBusy, setHealthBusy] = useState(false);

  const load = async (query = "") => {
    try {
      const r = await api.get("/admin/members", { params: { q: query } });
      setRows(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const sendReset = async (m) => {
    if (!window.confirm(`Send a password reset email to ${m.email}?`)) return;
    setBusyId(m.id);
    try { await api.post(`/admin/members/${m.id}/send-reset`); toast.success("Password reset email sent"); }
    catch (e) { toast.error(apiError(e)); }
    setBusyId(null);
  };

  const resendWelcome = async (m) => {
    if (!window.confirm(`Resend the welcome email (with a new set-password link) to ${m.email}?`)) return;
    setBusyId(m.id);
    try { await api.post(`/admin/members/${m.id}/resend-welcome`); toast.success("Welcome email sent"); }
    catch (e) { toast.error(apiError(e)); }
    setBusyId(null);
  };

  const resendVerification = async (m) => {
    if (!window.confirm(`Resend a new 6-digit verification code to ${m.email}?`)) return;
    setBusyId(m.id);
    try {
      const r = await api.post(`/admin/members/${m.id}/resend-verification`);
      if (r.data && r.data.mocked) toast.success("Code generated (email service in mock mode — see server logs)");
      else toast.success("Verification code sent");
    } catch (e) { toast.error(apiError(e)); }
    setBusyId(null);
  };

  const revealCode = async (m) => {
    if (!window.confirm(`Generate a fresh verification code for ${m.email} and REVEAL it on screen so you can read it to the customer? (This bypasses email entirely.)`)) return;
    setBusyId(m.id);
    try {
      const r = await api.post(`/admin/members/${m.id}/reveal-verification-code`);
      setRevealedCode(r.data);
    } catch (e) { toast.error(apiError(e)); }
    setBusyId(null);
  };

  const runEmailHealthCheck = async () => {
    setHealthBusy(true); setEmailHealth(null);
    try {
      const r = await api.get("/admin/diagnostics/email-health");
      setEmailHealth(r.data);
    } catch (e) { toast.error(apiError(e)); }
    setHealthBusy(false);
  };

  return (
    <div data-testid="admin-members">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="font-display font-bold text-xl flex items-center gap-2"><Users size={20} /> Members</h2>
          <button onClick={() => setShowAdd(true)} data-testid="member-add-open"
            className="bg-emerald-700 text-white text-sm font-bold px-4 py-2 rounded-xl hover:bg-emerald-800 transition-colors">+ Register member</button>
          <button onClick={runEmailHealthCheck} disabled={healthBusy} data-testid="email-health-check-btn"
            className="inline-flex items-center gap-1.5 bg-slate-900 text-white text-sm font-bold px-4 py-2 rounded-xl hover:bg-slate-800 transition-colors disabled:opacity-50">
            <Activity size={14} /> {healthBusy ? "Checking…" : "Email delivery check"}
          </button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); load(q); }} className="flex gap-2">
          <input className={`${inputCls} sm:w-72`} placeholder="Search name or email…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="member-search-input" />
          <button type="submit" className="bg-slate-900 text-white text-sm font-bold px-4 py-2 rounded-xl" data-testid="member-search-btn">Search</button>
        </form>
      </div>

      <div className="space-y-3">
        {rows.map((m) => (
          <div key={m.id} className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 flex flex-col md:flex-row md:items-center gap-3" data-testid={`member-row-${m.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-bold">{m.name || "—"}</p>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${BADGE[m.membership] || BADGE.free}`}>{m.membership_label || m.membership}</span>
                {m.role === "admin" && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-slate-900 text-white">Admin</span>}
                {m.manual && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-violet-100 text-violet-700"><BadgeCheck size={11} /> Manually activated</span>}
                {!m.manual && m.email_verified === false && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid={`member-unverified-${m.id}`}>Unverified</span>}
              </div>
              <p className="text-sm text-slate-500 mt-0.5">{m.email}</p>
              <p className="text-xs text-slate-400 mt-0.5">
                Joined {m.created_at ? m.created_at.slice(0, 10) : "—"}
                {m.renewal_date && <> • Renews {m.renewal_date}</>}
                {m.manual && m.payment_received != null && <> • Payment received: {m.payment_received ? "yes" : "no"}</>}
                {m.payment_reference && <> • Ref: {m.payment_reference}</>}
                {m.voucher_code && <> • Voucher: {m.voucher_code}</>}
              </p>
            </div>
            {m.role !== "admin" && (
              <div className="flex gap-2 shrink-0 flex-wrap">
                <button onClick={() => setEditFor(m)} data-testid={`member-edit-${m.id}`}
                  className="inline-flex items-center gap-1.5 text-xs font-bold border border-slate-200 px-3.5 py-2 rounded-lg hover:bg-slate-50 transition-colors">
                  <Pencil size={12} /> Edit
                </button>
                <button onClick={() => setPlanFor(m)} data-testid={`member-plan-${m.id}`}
                  className="text-xs font-bold border border-slate-200 px-3.5 py-2 rounded-lg hover:bg-slate-50 transition-colors">Change plan</button>
                {m.email_verified === false && !m.manual && (
                  <>
                    <button onClick={() => resendVerification(m)} disabled={busyId === m.id} data-testid={`member-resend-code-${m.id}`}
                      className="inline-flex items-center gap-1.5 text-xs font-bold border border-amber-300 text-amber-800 bg-amber-50 px-3.5 py-2 rounded-lg hover:bg-amber-100 transition-colors disabled:opacity-50">
                      <Mail size={12} /> Resend verification code
                    </button>
                    <button onClick={() => revealCode(m)} disabled={busyId === m.id} data-testid={`member-reveal-code-${m.id}`}
                      className="inline-flex items-center gap-1.5 text-xs font-bold border border-rose-300 text-rose-700 bg-rose-50 px-3.5 py-2 rounded-lg hover:bg-rose-100 transition-colors disabled:opacity-50"
                      title="Last-resort: generates a fresh code and shows it on screen so you can read it to the customer by phone or chat (bypasses email).">
                      <Eye size={12} /> Reveal code
                    </button>
                  </>
                )}
                {m.membership !== "free" && (
                  <button onClick={() => resendWelcome(m)} disabled={busyId === m.id} data-testid={`member-welcome-${m.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-bold border border-emerald-200 text-emerald-700 px-3.5 py-2 rounded-lg hover:bg-emerald-50 transition-colors disabled:opacity-50">
                    <Mail size={12} /> Resend welcome
                  </button>
                )}
                <button onClick={() => sendReset(m)} disabled={busyId === m.id} data-testid={`member-reset-${m.id}`}
                  className="inline-flex items-center gap-1.5 text-xs font-bold border border-slate-200 px-3.5 py-2 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50">
                  <KeyRound size={12} /> Send password reset
                </button>
              </div>
            )}
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-slate-400 py-8 text-center">No members found.</p>}
      </div>

      {showAdd && <AddMemberModal onSaved={() => load(q)} onClose={() => setShowAdd(false)} />}
      {planFor && <PlanModal member={planFor} onSaved={() => load(q)} onClose={() => setPlanFor(null)} />}
      {editFor && <EditMemberModal member={editFor} onSaved={() => load(q)} onClose={() => setEditFor(null)} />}

      {revealedCode && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="reveal-code-modal">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold text-lg">Verification code — read this to the customer</h3>
              <button onClick={() => setRevealedCode(null)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <p className="text-xs text-slate-500 mb-3">For <b>{revealedCode.email}</b> · valid for 30 minutes</p>
            <div className="bg-slate-900 text-amber-400 font-mono text-4xl font-extrabold tracking-[0.4em] text-center py-6 rounded-xl select-all" data-testid="revealed-code-value">
              {revealedCode.code}
            </div>
            <button
              onClick={() => { navigator.clipboard.writeText(revealedCode.code); toast.success("Code copied"); }}
              className="w-full mt-3 inline-flex items-center justify-center gap-1.5 bg-slate-100 text-slate-700 font-bold text-sm py-2.5 rounded-xl hover:bg-slate-200 transition-colors">
              <Copy size={14} /> Copy code
            </button>
            <p className="text-[11px] text-slate-400 mt-3 leading-relaxed">
              Ask the customer to open the "Verify your email" screen (they can click <b>Resend code</b> if closed, then
              enter this 6-digit code). This action was logged for the audit trail.
            </p>
          </div>
        </div>
      )}

      {emailHealth && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="email-health-modal">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold text-lg flex items-center gap-2"><Activity size={18} /> Email delivery check</h3>
              <button onClick={() => setEmailHealth(null)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <div className={`rounded-xl px-4 py-3 mb-3 text-sm font-bold ${
              emailHealth.status === "ok" ? "bg-emerald-50 text-emerald-800 border border-emerald-200" :
              emailHealth.status === "mocked" ? "bg-slate-100 text-slate-700 border border-slate-200" :
              "bg-rose-50 text-rose-800 border border-rose-200"
            }`} data-testid="email-health-status">
              Status: {emailHealth.status.toUpperCase()}
            </div>
            <dl className="text-sm space-y-2">
              <div className="flex gap-2"><dt className="font-bold text-slate-500 w-32">Server IP:</dt>
                <dd className="font-mono text-slate-800">{emailHealth.egress_ip || "—"}</dd></div>
              <div className="flex gap-2"><dt className="font-bold text-slate-500 w-32">Sender email:</dt>
                <dd className="font-mono text-slate-800">{emailHealth.sender_email}</dd></div>
              <div className="flex gap-2"><dt className="font-bold text-slate-500 w-32">Brevo key set:</dt>
                <dd>{emailHealth.has_brevo_key ? "✅ yes" : "❌ no"}</dd></div>
              <div className="flex gap-2"><dt className="font-bold text-slate-500 w-32">Tested to:</dt>
                <dd className="font-mono text-slate-800">{emailHealth.admin_email}</dd></div>
            </dl>
            {emailHealth.hint && (
              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-900 leading-relaxed" data-testid="email-health-hint">
                {emailHealth.hint}
              </div>
            )}
            <details className="mt-3">
              <summary className="text-xs text-slate-400 cursor-pointer">Raw provider response</summary>
              <pre className="text-[11px] bg-slate-50 p-2 rounded mt-1 overflow-x-auto">{JSON.stringify(emailHealth.brevo_response, null, 2)}</pre>
            </details>
          </div>
        </div>
      )}
    </div>
  );
}
